import React, {createContext, useContext, useState, useCallback} from 'react';
import {ToastAlert, ToastContainerWrapper} from './ToastAlert';
import {AlertContextValue, ToastAlertProps, AlertPosition} from './types';

const AlertContext = createContext<AlertContextValue | undefined>(undefined);

interface ToastItem extends ToastAlertProps {
  id: string;
}

interface AlertProviderProps {
  children: React.ReactNode;
  defaultPosition?: AlertPosition;
}

export const AlertProvider: React.FC<AlertProviderProps> = ({ 
    children, 
    defaultPosition = 'top-center' 
}) => {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    const generateId = useCallback(() => {
        return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    }, []);

    const showToast = useCallback((props: Omit<ToastAlertProps, 'variant'> & { variant?: ToastAlertProps['variant'] }) => {
        const id = generateId();
        const toast: ToastItem = {
            variant: 'info',
            position: defaultPosition,
            ...props,
            id
        };

        setToasts(prev => [...prev, toast]);
        return id;
    }, [generateId, defaultPosition]);

    const showSuccess = useCallback((message: string, options: Partial<ToastAlertProps> = {}) => {
        return showToast({...options, message, variant: 'success'});
    }, [showToast]);

    const showError = useCallback((message: string, options: Partial<ToastAlertProps> = {}) => {
        return showToast({...options, message, variant: 'error'});
    }, [showToast]);

    const showWarning = useCallback((message: string, options: Partial<ToastAlertProps> = {}) => {
        return showToast({...options, message, variant: 'warning'});
    }, [showToast]);

    const showInfo = useCallback((message: string, options: Partial<ToastAlertProps> = {}) => {
        return showToast({...options, message, variant: 'info'});
    }, [showToast]);

    const dismissToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const dismissAll = useCallback(() => {
        setToasts([]);
    }, []);

    const contextValue: AlertContextValue = {
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        dismissToast,
        dismissAll
    };

    const groupedToasts = toasts.reduce((acc, toast) => {
        const position = toast.position || defaultPosition;
        if (!acc[position]) {
            acc[position] = [];
        }
        acc[position].push(toast);
        return acc;
    }, {} as Record<AlertPosition, ToastItem[]>);

    return (
        <AlertContext.Provider value={contextValue}>
            {children}
            {Object.entries(groupedToasts).map(([position, positionToasts]) => (
                <ToastContainerWrapper key={position} position={position as AlertPosition}>
                    {positionToasts.map(toast => (
                        <ToastAlert
                            key={toast.id}
                            {...toast}
                            onClose={() => dismissToast(toast.id)}
                        />
                    ))}
                </ToastContainerWrapper>
            ))}
        </AlertContext.Provider>
    );
};

export const useAlert = (): AlertContextValue => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};
